const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const normalizeLoad = (load) => {
  const numeric = toNumber(load);
  if (numeric === null) return 0;
  if (numeric <= 1) return clamp(numeric * 100, 0, 100);
  return clamp(numeric, 0, 100);
};

const hexToRgb = (hex) => {
  const clean = String(hex || '').replace('#', '').trim();
  if (clean.length !== 6) return { r: 6, g: 182, b: 212 };
  return {
    r: parseInt(clean.slice(0, 2), 16),
    g: parseInt(clean.slice(2, 4), 16),
    b: parseInt(clean.slice(4, 6), 16),
  };
};

const rgbToHex = ({ r, g, b }) => {
  const toHex = (value) => clamp(Math.round(value), 0, 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const interpolateColor = (from, to, ratio) => {
  const left = hexToRgb(from);
  const right = hexToRgb(to);
  return rgbToHex({
    r: left.r + (right.r - left.r) * ratio,
    g: left.g + (right.g - left.g) * ratio,
    b: left.b + (right.b - left.b) * ratio,
  });
};

export const getBandwidthStyle = (load, options = {}) => {
  const {
    status = 'online',
    useGradient = true,
    lowColor = '#22d3ee',
    mediumColor = '#f59e0b',
    highColor = '#ef4444',
    offlineColor = '#ef4444',
    unknownColor = '#94a3b8',
    minWeight = 2,
    maxWeight = 7,
    weightBoost = 0,
  } = options;

  const normalized = normalizeLoad(load);
  const state = String(status || '').toLowerCase();
  if (state === 'offline') {
    return {
      load: normalized,
      color: offlineColor,
      weight: minWeight + weightBoost,
      glowClass: 'bandwidth-glow-high',
      severity: 'offline',
    };
  }

  const baseWeight = minWeight + ((maxWeight - minWeight) * normalized) / 100;
  const weight = clamp(baseWeight + weightBoost, minWeight, maxWeight + weightBoost);
  let color = unknownColor;
  let severity = 'low';
  let glowClass = 'bandwidth-glow-low';

  if (normalized >= 80) {
    severity = 'high';
    glowClass = 'bandwidth-glow-high';
    color = useGradient ? interpolateColor(mediumColor, highColor, (normalized - 80) / 20) : highColor;
  } else if (normalized >= 50) {
    severity = 'medium';
    glowClass = 'bandwidth-glow-medium';
    color = useGradient ? interpolateColor(lowColor, mediumColor, (normalized - 50) / 30) : mediumColor;
  } else if (normalized > 0) {
    severity = 'low';
    glowClass = 'bandwidth-glow-low';
    color = useGradient ? interpolateColor(lowColor, mediumColor, normalized / 50) : lowColor;
  } else if (state === 'online') {
    color = lowColor;
  }

  return {
    load: normalized,
    color,
    weight,
    glowClass,
    severity,
  };
};

export const resolveLinkLoad = (payload = {}) => {
  const candidates = [
    payload.load_percent,
    payload.bandwidth_load_percent,
    payload.traffic_load_percent,
    payload.traffic_load,
    payload.bandwidth_load,
    payload.current_load,
    payload.load,
  ];
  for (const candidate of candidates) {
    const numeric = toNumber(candidate);
    if (numeric !== null) return normalizeLoad(numeric);
  }
  return 0;
};
